/**
 * Rapid X - Unified Time & Attendance Page
 * 
 * ARCHITECTURE: Attendance is now driven by Shift activity.
 * - Check-in/out is done via the Shifts page
 * - This page shows attendance history and current status based on shifts
 * - Officers are directed to Shifts for actual check-in/out actions
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { formatDate, formatTime } from '../lib/utils';
import { 
  Clock, MapPin, CheckCircle2, XCircle, 
  Loader2, AlertTriangle, History, Timer, Building2,
  Calendar, ArrowRight, LogIn, LogOut, QrCode
} from 'lucide-react';

export default function AttendancePage() {
  const { api, user, isAdmin, isOfficer } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [currentShift, setCurrentShift] = useState(null);
  const [todayShifts, setTodayShifts] = useState([]);
  const [recentAttendance, setRecentAttendance] = useState([]);
  const [stats, setStats] = useState({
    total_hours_this_week: 0,
    shifts_this_week: 0,
    on_time_percentage: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Get today's shifts for the current user
      const shiftsRes = await api.get('/shifts?today_only=true&limit=20');
      const allShifts = shiftsRes.data?.shifts || shiftsRes.data || [];
      
      // Filter to user's shifts
      const myShifts = allShifts.filter(s => s.officer_id === user?.id);
      setTodayShifts(myShifts);
      
      // Find current active shift (checked in but not out)
      const active = myShifts.find(s => s.checked_in && !s.check_out_time);
      setCurrentShift(active);
      
      // Get recent attendance (completed shifts with check-in/out)
      try {
        const historyRes = await api.get('/shifts?limit=20');
        const historyShifts = (historyRes.data?.shifts || historyRes.data || [])
          .filter(s => s.officer_id === user?.id && s.checked_in)
          .slice(0, 10);
        setRecentAttendance(historyShifts);
        
        // Calculate stats
        const thisWeekShifts = historyShifts.filter(s => {
          const shiftDate = new Date(s.start_time);
          const now = new Date();
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return shiftDate >= weekAgo;
        });
        
        const totalHours = thisWeekShifts.reduce((acc, s) => {
          if (s.check_in_time && s.check_out_time) {
            const checkIn = new Date(s.check_in_time);
            const checkOut = new Date(s.check_out_time);
            return acc + (checkOut - checkIn) / (1000 * 60 * 60);
          }
          return acc;
        }, 0);
        
        setStats({
          total_hours_this_week: totalHours.toFixed(1),
          shifts_this_week: thisWeekShifts.length,
          on_time_percentage: thisWeekShifts.length > 0 
            ? Math.round((thisWeekShifts.filter(s => !s.is_exception).length / thisWeekShifts.length) * 100)
            : 100
        });
      } catch (e) {
        console.log('History fetch error:', e);
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (shift) => {
    if (shift.check_out_time) return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    if (shift.checked_in) return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
    return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
  };

  const getStatusText = (shift) => {
    if (shift.check_out_time) return 'Completed';
    if (shift.checked_in) return 'Checked In';
    return 'Scheduled';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="attendance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-rajdhani font-bold text-white tracking-wide">
            TIME & ATTENDANCE
          </h1>
          <p className="text-sm text-zinc-500">Your shift attendance and work hours</p>
        </div>
        
        <Button
          onClick={() => navigate('/shifts')}
          className="bg-blue-500 hover:bg-blue-600 text-white"
          data-testid="go-to-shifts-btn"
        >
          <Calendar className="w-4 h-4 mr-2" />
          Go to Shifts
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>

      {/* Current Status Card */}
      <Card className="bg-[#18181b] border-[#3f3f46]">
        <CardHeader className="pb-3">
          <CardTitle className="text-white font-rajdhani flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-400" />
            Current Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          {currentShift ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <p className="text-white font-medium">Currently Checked In</p>
                  <p className="text-zinc-400 text-sm">{currentShift.site_name}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-zinc-500">Check-in Time:</span>
                  <span className="ml-2 text-white">{formatTime(currentShift.check_in_time)}</span>
                </div>
                <div>
                  <span className="text-zinc-500">Shift Ends:</span>
                  <span className="ml-2 text-white">{formatTime(currentShift.end_time)}</span>
                </div>
              </div>
              
              <Button
                onClick={() => navigate('/shifts')}
                variant="outline"
                className="w-full border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Go to Shifts to Check Out
              </Button>
            </div>
          ) : todayShifts.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-white font-medium">You have {todayShifts.length} shift(s) today</p>
                  <p className="text-zinc-400 text-sm">Use the Shifts page to check in</p>
                </div>
              </div>
              
              <div className="space-y-2">
                {todayShifts.slice(0, 3).map(shift => (
                  <div 
                    key={shift.id}
                    className="p-3 bg-[#09090b] rounded border border-[#3f3f46] flex items-center justify-between"
                  >
                    <div>
                      <p className="text-white text-sm font-medium">{shift.site_name}</p>
                      <p className="text-zinc-500 text-xs">
                        {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                      </p>
                    </div>
                    <Badge className={`${getStatusColor(shift)} border`}>
                      {getStatusText(shift)}
                    </Badge>
                  </div>
                ))}
              </div>
              
              <Button
                onClick={() => navigate('/shifts')}
                className="w-full bg-emerald-500 hover:bg-emerald-600"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Go to Shifts to Check In
              </Button>
            </div>
          ) : (
            <div className="text-center py-6">
              <XCircle className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">No shifts scheduled for today</p>
              <Button
                onClick={() => navigate('/roster')}
                variant="outline"
                className="mt-4 border-zinc-600"
              >
                View Roster
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Timer className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total_hours_this_week}h</p>
                <p className="text-xs text-zinc-500">Hours This Week</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.shifts_this_week}</p>
                <p className="text-xs text-zinc-500">Shifts This Week</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#18181b] border-[#3f3f46]">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.on_time_percentage}%</p>
                <p className="text-xs text-zinc-500">On-Time Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-500/10 border-blue-500/30">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
              <QrCode className="w-4 h-4 text-blue-400" />
            </div>
            <div>
              <p className="text-blue-400 font-medium">Unified Check-in System</p>
              <p className="text-blue-300/80 text-sm mt-1">
                All check-ins and check-outs are now managed through the Shifts page. 
                You can use GPS or scan the site QR code to verify your location when checking in.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Attendance History */}
      <Card className="bg-[#18181b] border-[#3f3f46]">
        <CardHeader className="pb-3">
          <CardTitle className="text-white font-rajdhani flex items-center gap-2">
            <History className="w-5 h-5 text-zinc-400" />
            Recent Attendance
          </CardTitle>
          <CardDescription className="text-zinc-500">
            Your recent shift attendance history
          </CardDescription>
        </CardHeader>
        <CardContent>
          {recentAttendance.length === 0 ? (
            <div className="text-center py-6">
              <History className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
              <p className="text-zinc-500 text-sm">No attendance records yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentAttendance.map(record => (
                <div 
                  key={record.id}
                  className="p-3 bg-[#09090b] rounded border border-[#3f3f46] flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-zinc-400" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">{record.site_name}</p>
                      <p className="text-zinc-500 text-xs">
                        {formatDate(record.start_time)} • {formatTime(record.check_in_time)} - {record.check_out_time ? formatTime(record.check_out_time) : 'In Progress'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {record.is_exception && (
                      <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 text-xs">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Exception
                      </Badge>
                    )}
                    <Badge className={`${getStatusColor(record)} border text-xs`}>
                      {getStatusText(record)}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
